import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { endOfMonth, startOfMonth } from 'date-fns';
import { Text } from 'office-ui-fabric-react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { Dropdown } from 'office-ui-fabric-react/lib/Dropdown';
import { addMonths } from 'office-ui-fabric-react/lib/utilities/dateMath/DateMath';
import { removeEmptyProperties } from '../../utils';
import StatementList from '../../components/StatementList';
import Module from '../../components/Module';
import ModuleContentList from '../../components/ModuleContentList';
import PageTitle from '../../components/PageTitle';
import Main from '../../components/Main';
import SearchBar from '../../components/SearchBar';
import Pagination from '../../components/Pagination';
import StatementActionRequired from '../../components/StatementActionRequired';
import { fetchStatements, fetchAttachment, fetchPayGoAttachment } from '../../actions/statements';
import { fetchTasks } from '../../actions/tasks';
import { can } from '../../utils/auth';
import Protected from '../../components/Protected';
import CalendarPicker from '../../components/CalendarPicker';
import theme from '../../theme.style';
import styles from './StatementListing.style';

const StatementListing = () => {
  const dispatch = useDispatch();
  const dateNow = new Date();
  const [maxDate] = useState(addMonths(dateNow, 0));
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = React.useState(0);
  const [filterState, setFilterState] = useState({});
  const [selectedStatements, setSelectedStatements] = useState([]);
  const [selectAllStatements, setSelectAllStatements] = useState(false);
  const [resetPagination, setResetPagination] = useState(false);
  let date;
  const [selectedDate, setSelectedDate] = React.useState(date);

  const statements = useSelector(state =>
    get(state, 'pagination.statements.results', [])
      .map(tokenId => get(state, `entities.statements.${tokenId}`))
      .sort((a, b) => b.statementEndDate - a.statementEndDate),
  );

  const tasks = useSelector(state => Object.values(get(state, 'entities.tasks', {}))).filter(
    task => task.area === 'statements',
  );

  const filterStatements = async query => {
    setResetPagination(true);
    setLoading(true);
    const response = await dispatch(fetchStatements(query));
    setLoading(false);
    setTotalCount(get(response, `meta.totalCount`, 0));
  };

  useEffect(() => {
    filterStatements();
    if (can('viewTasks')) {
      dispatch(fetchTasks(['statements']));
    }
    setShowSearchResults(false);
  }, [dispatch]);

  const onDownload = async downloadUrls => {
    const statementUrl = get(downloadUrls, 'excelFileUrl');
    const payGoStatementUrl = get(downloadUrls, 'payGoExcelFileUrl');
    if (statementUrl) {
      await dispatch(fetchAttachment(statementUrl));
    }
    if (payGoStatementUrl) {
      await dispatch(fetchPayGoAttachment(payGoStatementUrl));
    }
  };

  const onSelectStatement = statementId => {
    if (selectedStatements.length > 0 && selectedStatements.includes(statementId)) {
      setSelectedStatements(selectedStatements.filter(statement => statement !== statementId));
      // Uncheck the colum header select statements if the selected statements lengh becomes zero.
      setSelectAllStatements(false);
    } else {
      setSelectedStatements([...selectedStatements, statementId]);
      // Check the colum header select statements if the selected statements lengh becomes zero.
      if (selectedStatements.length === totalCount - 1) {
        setSelectAllStatements(true);
      }
    }
  };

  const updateStatementFilters = statementFilters => {
    const filters = removeEmptyProperties(statementFilters);
    if (isEmpty(filters)) {
      setShowSearchResults(false);
    } else {
      setShowSearchResults(true);
    }

    // Reset the select options
    setSelectedStatements([]);
    setSelectAllStatements(false);

    setFilterState(statementFilters);
    filterStatements(statementFilters);
  };

  const updateSearchFilter = async sValue => {
    const newFilterState = {
      ...filterState,
      q: sValue,
    };
    updateStatementFilters(newFilterState);
  };

  const handleClear = () => updateSearchFilter();
  const handleSearch = sValue => updateSearchFilter(sValue);

  const handlePageChange = async paginationData => {
    setResetPagination(false);
    setLoading(true);
    await dispatch(
      fetchStatements({
        ...filterState,
        start:
          paginationData.currentPageIndex === 0
            ? 1
            : paginationData.currentPageIndex * paginationData.pageLimit,
        count: paginationData.pageLimit,
      }),
    );
    setLoading(false);
  };

  const onStatusChange = async (event, option) => {
    const newFilterState = {
      ...filterState,
      status: option.status,
    };

    updateStatementFilters(newFilterState);
  };

  const filterOptions = [
    { key: 0, text: 'All Statements', status: '' },
    { key: 1, text: 'Approval In Progress', status: 'Generated' },
    {
      key: 2,
      text: 'Released to Partner',
      status: ['Approved', 'InvoiceGenerated', 'InvoiceGenerationSkipped', 'InvoiceScheduled'].join(
        ',',
      ),
    },
    {
      key: 3,
      text: 'Payment Pending',
      status: ['Approved', 'InvoiceScheduled'].join(','),
    },
    {
      key: 4,
      text: 'Paid',
      status: ['InvoiceGenerated', 'InvoiceGenerationSkipped'].join(','),
    },
    { key: 5, text: 'Public Preview', status: 'Preview' },
  ];

  const onSelectDate = calendarDate => {
    setSelectedDate(calendarDate);
    const newFilterState = {
      ...filterState,
      startDate: calendarDate ? startOfMonth(calendarDate) : '',
      endDate: calendarDate ? endOfMonth(calendarDate) : '',
    };

    updateStatementFilters(newFilterState);
  };

  const onHandleColumnClick = (event, column) => {
    switch (column.fieldName) {
      // eslint-disable-next-line default-case-last
      default:
        break;
      case 'select':
        setSelectedStatements([]);
        if (selectAllStatements) {
          setSelectAllStatements(false);
        } else {
          setSelectedStatements(statements.map(item => item._id));
          setSelectAllStatements(true);
        }
        break;
      case 'download': {
        selectedStatements.map(async statementId => {
          const downloadUrls = get(
            statements.find(item => item._id === statementId),
            'downloadUrls',
          );
          if (downloadUrls) {
            await onDownload(downloadUrls);
          }
        });
        break;
      }
    }
  };

  return (
    <>
      <PageTitle>Statements</PageTitle>
      <Main>
        {tasks?.length > 0 && (
          <Module>
            <ModuleContentList>
              <StatementActionRequired items={tasks} isHeaderVisible={false} />
            </ModuleContentList>
          </Module>
        )}
        <Protected permissions="filterStatements">
          <div className={styles.searchSection}>
            <SearchBar
              placeholder="Search statements"
              ariaLabel="Search"
              underlined
              onChange={handleSearch}
              onClear={handleClear}
              data-testid="search-input"
              className={styles.search}
            />
            <CalendarPicker
              showMonthPickerAsOverlay={false}
              isDayPickerVisible={false}
              isMonthPickerVisible
              showGoToToday={false}
              buttonString="Select"
              highlightCurrentMonth
              highlightSelectedMonth={false}
              selectedDate={selectedDate}
              maxDate={maxDate}
              onSelectDate={onSelectDate}
            />
            <Dropdown
              placeholder="All Statements"
              label="Status"
              options={filterOptions}
              className={styles.filter}
              styles={{
                title: { backgroundColor: theme.palette.neutralWhite },
                dropdown: { fontWeight: 'normal' },
                dropdownItem: { fontWeight: 'normal' },
              }}
              onChange={onStatusChange}
            />
          </div>
        </Protected>
        {(() => {
          if (showSearchResults && statements.length === 0) {
            return (
              <div className={styles.resultsMessage}>
                <Text block>No Results</Text>
                <Text as="p" role="alert" block className={styles.noResultsSentence}>
                  Try searching by contract name, contract ID, partner name, contract period or
                  contract status
                </Text>
              </div>
            );
          }

          return (
            <>
              {!loading && (
                <div className={styles.resultsMessage}>
                  <Text block>
                    {totalCount} {totalCount === 1 ? 'Result' : 'Results'}
                  </Text>
                </div>
              )}

              <Module>
                <ModuleContentList>
                  <StatementList
                    items={statements}
                    shimmered
                    enableShimmer={loading}
                    onDownload={onDownload}
                    onHandleColumnClick={onHandleColumnClick}
                    onSelectStatement={onSelectStatement}
                    selectedStatements={selectedStatements}
                    selectAllStatements={selectAllStatements}
                  />
                </ModuleContentList>
                <Pagination
                  totalRecords={totalCount}
                  handlePageChange={handlePageChange}
                  resetPagination={resetPagination}
                />
              </Module>
            </>
          );
        })()}
      </Main>
    </>
  );
};

export default StatementListing;
